import { AccessTime, ArrowBack, Lock, PlayArrow } from "@mui/icons-material";
import { Alert, Box, Button, Chip, CircularProgress, Divider, Paper, Stack, Typography } from "@mui/material";
import { useTranslate } from "react-admin";
import { Link, useParams } from "react-router-dom";

import { useLearningCatalog, useLearningCoursePayload, useMyLearning } from "../../../hooks/useLearningCatalog";
import { resolveLearningMediaUrl } from "../../../lib/learning/mediaReference";
import { borderRadius, color, shadows, space } from "../../../theme/designTokens";

export const CourseDetail = () => {
  const translate = useTranslate();
  const { courseId } = useParams<{ courseId: string }>();
  const { courses, loading, error, reload } = useLearningCatalog();
  const course = courses.find(item => item.id === courseId);
  const learning = useMyLearning();
  const courseState = learning.courses.find(item => item.courseId === courseId);
  const activeVersion =
    courseState?.status === "IN_PROGRESS" && courseState.startedVersions.length > 0
      ? courseState.startedVersions[courseState.startedVersions.length - 1]
      : course?.version;
  const payload = useLearningCoursePayload(
    courseState && !course?.locked ? course?.id : undefined,
    learning.loading ? undefined : activeVersion
  );
  const firstIncompleteLesson = payload.course?.lessons.find(
    lesson => !courseState?.completedLessonIds.includes(lesson.id)
  );
  const resumeTarget = firstIncompleteLesson
    ? `/learning/courses/${course?.id ?? ""}/lessons/${firstIncompleteLesson.id}`
    : `/learning/courses/${course?.id ?? ""}/quiz`;

  if ((loading && !course) || learning.loading || (course && payload.loading && !payload.course)) {
    return (
      <Box display="grid" minHeight={320} sx={{ placeItems: "center" }}>
        <CircularProgress aria-label={translate("learning.catalog.loading")} />
      </Box>
    );
  }
  if ((error && !course) || learning.error || (course && payload.error)) {
    return (
      <Alert
        severity="error"
        action={
          <Button onClick={() => void (course && payload.error ? payload.reload() : reload())}>
            {translate("learning.retry")}
          </Button>
        }
      >
        {translate(course && payload.error ? "learning.course.contentError" : "learning.catalog.error")}
      </Alert>
    );
  }
  if (!course) return <Alert severity="warning">{translate("learning.course.notFound")}</Alert>;

  const startLabel =
    courseState?.status === "COMPLETED"
      ? "learning.course.repeat"
      : courseState?.status === "IN_PROGRESS"
        ? "learning.course.continue"
        : "learning.course.start";

  let thumbnail: string | undefined;
  try {
    thumbnail = resolveLearningMediaUrl(course.thumbnailFilename);
  } catch {
    thumbnail = undefined;
  }

  return (
    <Stack spacing={space.xxl}>
      <Button component={Link} to="/learning" startIcon={<ArrowBack />} sx={{ alignSelf: "flex-start" }}>
        {translate("learning.backToCatalog")}
      </Button>
      <Paper
        variant="outlined"
        sx={{
          overflow: "hidden",
          borderRadius: borderRadius.md,
          borderColor: color.panelBorder,
          boxShadow: shadows.card,
        }}
      >
        {thumbnail && (
          <Box component="img" src={thumbnail} alt="" sx={{ width: "100%", maxHeight: 360, objectFit: "cover" }} />
        )}
        <Stack spacing={space.xl} p={{ xs: space.xl, md: space.xxl1 }}>
          <Box>
            <Typography component="h1" variant="h4" gutterBottom>
              {course.title}
            </Typography>
            <Typography color="text.secondary">{course.description}</Typography>
          </Box>
          <Stack direction="row" gap={space.md} flexWrap="wrap">
            <Chip
              icon={<AccessTime />}
              label={translate("learning.duration", { minutes: course.estimatedDurationMinutes })}
            />
            {course.tags.map(tag => (
              <Chip key={tag} label={tag} variant="outlined" />
            ))}
            {course.locked && <Chip icon={<Lock />} label={translate("learning.locked")} />}
            {courseState?.status === "COMPLETED" && <Chip color="success" label={translate("learning.completed")} />}
          </Stack>
          <Divider />
          {payload.course && (
            <Box>
              <Typography component="h2" variant="h5" gutterBottom>
                {translate("learning.course.lessons")}
              </Typography>
              <Typography color="text.secondary">
                {translate("learning.course.lessonCount", { count: payload.course.lessons.length })}
              </Typography>
              <Stack component="ol" spacing={space.md} sx={{ pl: space.xxl }}>
                {payload.course.lessons.map(lesson => (
                  <Typography component="li" key={lesson.id}>
                    {lesson.title}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}
          {!course.directAccess && !course.pathAccess ? (
            <Alert severity="info" data-testid="learning-course-not-assigned">
              {translate("learning.course.notAssigned")}
            </Alert>
          ) : course.locked ? (
            <Alert severity="info">{translate("learning.course.lockedHelp")}</Alert>
          ) : !courseState || !payload.course ? (
            <Alert severity="warning">{translate("learning.player.notAvailable")}</Alert>
          ) : (
            <Button
              data-testid="learning-course-start"
              component={Link}
              to={
                courseState?.status === "COMPLETED"
                  ? `/learning/courses/${course.id}/lessons/${payload.course?.lessons[0]?.id ?? ""}`
                  : resumeTarget
              }
              variant="contained"
              startIcon={<PlayArrow />}
              sx={{ alignSelf: "flex-start" }}
            >
              {translate(startLabel)}
            </Button>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
};
